import { useState } from "react";
import { TemplateCard } from "./TemplateCard";
import { TemplateListRow } from "./TemplateListRow";
import { ThumbnailWarmer } from "./ThumbnailWarmer";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useTemplatesQuery } from "@/api/templates";
import type { TemplateListParams } from "@/types/template";

type ViewMode = "grid" | "list";

const CATEGORIES = [
  "All",
  "Lower Thirds",
  "Intros",
  "Overlays",
  "Titles",
  "Transitions",
  "Alerts",
  "Other",
];

type FilterTab = "mine" | "shared" | "public";

const TABS: { key: FilterTab; label: string }[] = [
  { key: "mine", label: "My Templates" },
  { key: "shared", label: "Shared with me" },
  { key: "public", label: "Public" },
];

export function TemplateBrowser() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [activeTab, setActiveTab] = useState<FilterTab>("mine");
  const [viewMode, setViewMode] = useLocalStorage<ViewMode>(
    "motionforge-templates-view",
    "grid",
  );

  const params: TemplateListParams = {
    search: search || undefined,
    // DB stores category as a slug ("lower-thirds"), the dropdown shows
    // display labels ("Lower Thirds") — convert before querying.
    category:
      category === "All"
        ? undefined
        : category.toLowerCase().replace(/\s+/g, "-"),
    filter: activeTab,
    // No pagination UI yet — fetch enough that nothing silently falls off
    // the backend's default limit of 20.
    limit: 500,
  };

  const { data, isLoading } = useTemplatesQuery(params);

  return (
    <div className="flex flex-col gap-6">
      <ThumbnailWarmer templates={data} />
      {/* Tabs */}
      <div className="flex gap-1 rounded-lg border border-line bg-surface-2 p-1">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? "bg-surface-3 text-accent"
                : "text-ink-2 hover:text-ink"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1">
          <svg
            className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search templates..."
            className="w-full rounded-lg border border-line bg-surface py-2 pl-10 pr-4 text-sm text-ink placeholder:text-ink-3 focus:border-line-strong focus:outline-none"
          />
        </div>

        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="rounded-lg border border-line bg-surface px-4 py-2 text-sm text-ink focus:border-line-strong focus:outline-none"
        >
          {CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>

        {/* View toggle — Grid (default, thumbnail-rich) ↔ List (compact
            row-per-template, easier to scan when you have many). Choice
            persists per browser. */}
        <div className="flex rounded-lg border border-line bg-surface-2 p-0.5">
          <button
            type="button"
            onClick={() => setViewMode("grid")}
            aria-label="Grid view"
            title="Grid view"
            className={`flex h-9 w-9 items-center justify-center rounded text-base transition-colors ${
              viewMode === "grid"
                ? "bg-surface-3 text-accent"
                : "text-ink-2 hover:text-ink"
            }`}
          >
            ▦
          </button>
          <button
            type="button"
            onClick={() => setViewMode("list")}
            aria-label="List view"
            title="List view"
            className={`flex h-9 w-9 items-center justify-center rounded text-base transition-colors ${
              viewMode === "list"
                ? "bg-surface-3 text-accent"
                : "text-ink-2 hover:text-ink"
            }`}
          >
            ☰
          </button>
        </div>
      </div>

      {/* Body — grid or list based on viewMode */}
      {isLoading ? (
        viewMode === "grid" ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[4/3] animate-pulse rounded-lg border border-line bg-surface-2"
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-16 animate-pulse rounded-md border border-line bg-surface-2"
              />
            ))}
          </div>
        )
      ) : data && data.length > 0 ? (
        viewMode === "grid" ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {data.map((template) => (
              <TemplateCard key={template.id} template={template} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {data.map((template) => (
              <TemplateListRow key={template.id} template={template} />
            ))}
          </div>
        )
      ) : (
        <div className="py-16 text-center text-sm text-ink-3">
          {activeTab === "mine"
            ? "You haven't created any templates yet."
            : activeTab === "shared"
              ? "No templates have been shared with you."
              : "No public templates available."}
        </div>
      )}
    </div>
  );
}
